/* Perceptual (OKLab/OKLCH) color helpers for the dev theme playground.
 *
 * Deriving the `-text-emphasis` / `-bg-subtle` / `-border-subtle` tokens from a
 * base hue by moving lightness in OKLCH (while preserving hue and scaling
 * chroma) keeps colors saturated - matching the hand-tuned look of index.css -
 * where Bootstrap's white/black sRGB mixing would wash them out. Conversions
 * use Björn Ottosson's OKLab matrices; out-of-gamut results are pulled back into
 * sRGB by reducing chroma (a binary search) rather than clipping channels, so
 * the hue is preserved. */

export type Oklch = { L: number; C: number; H: number };

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const h = (v: number) =>
    Math.round(clamp(v, 0, 255))
      .toString(16)
      .padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
}

function linearToOklab([r, g, b]: [number, number, number]): [number, number, number] {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  return [
    0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  ];
}

function oklabToLinear([L, a, b]: [number, number, number]): [number, number, number] {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

export function hexToOklch(hex: string): Oklch | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const lin = rgb.map((v) => srgbToLinear(v / 255)) as [number, number, number];
  const [L, a, b] = linearToOklab(lin);
  return { L, C: Math.hypot(a, b), H: Math.atan2(b, a) };
}

function oklchToLinear({ L, C, H }: Oklch): [number, number, number] {
  return oklabToLinear([L, C * Math.cos(H), C * Math.sin(H)]);
}

function inGamut([r, g, b]: [number, number, number]): boolean {
  const eps = 1e-4;
  return [r, g, b].every((v) => v >= -eps && v <= 1 + eps);
}

export function oklchToHex(color: Oklch): string {
  const L = clamp(color.L, 0, 1);
  let C = Math.max(0, color.C);
  const H = color.H;

  // If the requested chroma is unreachable at this lightness, shrink it until
  // the color fits sRGB - preserving hue instead of clipping channels.
  if (!inGamut(oklchToLinear({ L, C, H }))) {
    let lo = 0;
    let hi = C;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(oklchToLinear({ L, C: mid, H }))) lo = mid;
      else hi = mid;
    }
    C = lo;
  }

  const [r, g, b] = oklchToLinear({ L, C, H }).map((v) => linearToSrgb(clamp(v, 0, 1))) as [
    number,
    number,
    number,
  ];
  return rgbToHex(r * 255, g * 255, b * 255);
}

export type SubtleRole = "text-emphasis" | "bg-subtle" | "border-subtle";

/** Target OKLCH lightness (absolute) and chroma (as a fraction of the base's
 * chroma) for each derived token, per theme. Tuned to sit near the existing
 * hand-picked tokens in index.css. */
const TARGETS: Record<"light" | "dark", Record<SubtleRole, { l: number; c: number }>> = {
  light: {
    "text-emphasis": { l: 0.4, c: 0.7 },
    "border-subtle": { l: 0.82, c: 0.5 },
    "bg-subtle": { l: 0.94, c: 0.3 },
  },
  dark: {
    "text-emphasis": { l: 0.84, c: 0.5 },
    "border-subtle": { l: 0.46, c: 0.65 },
    "bg-subtle": { l: 0.27, c: 0.45 },
  },
};

/** Derive the three subtle tokens from a base hex for the given theme. Returns
 * null if the base isn't a valid 6-digit hex color. */
export function deriveSubtle(
  baseHex: string,
  theme: "light" | "dark",
): Record<SubtleRole, string> | null {
  const base = hexToOklch(baseHex);
  if (!base) return null;
  const targets = TARGETS[theme];
  const make = (role: SubtleRole) =>
    oklchToHex({ L: targets[role].l, C: base.C * targets[role].c, H: base.H });
  return {
    "text-emphasis": make("text-emphasis"),
    "bg-subtle": make("bg-subtle"),
    "border-subtle": make("border-subtle"),
  };
}
